import { useState, useEffect, useRef } from "react";

import { useTranslation, withTranslation } from "react-i18next";
import { Box, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, TextField, Typography, Grid } from "@mui/material";
import BaseConfigModal from "@plannel/components/modal/BaseConfigModal";
import AppCard from "@plannel/components/ui/AppCard";
import CardContent from "@mui/material/CardContent";
import Snackbar from "@plannel/components/Snackbar";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import dateUtils from "@plannel/utils/date-util";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { isEmpty, isEqual, size, values, includes } from "lodash";
import debug from "@plannel/utils/debug-util";

import planVersionService from "@plannel/services/data/plan-version-service";
import planConfigService from "@plannel/services/data/plan-config-service";
import measureService from "@plannel/services/data/measure-service";
import hrchyConfigService from "@plannel/services/data/hrchy-config-service";
import Dialog from "@plannel/components/Dialog";
import codeUtil from "@plannel/pages/utils/code-util";
import PlanHorizonSelect from "../dp-config/PlanHorizonSelect";
import ColorUtil from "@plannel/pages/utils/color-util";
import ComputeMeasureModal from "../dp-workbench/dp-workbench-modal/ComputeMeasureModal";
import { CalculationButton, DetailButton } from "@plannel/components/ActionIconButton";
import { PanelField, PANEL_FORM_SX, PANEL_CHECKBOX_SX } from "@plannel/components/ui/PanelField";
import WorkbenchUtil from "../dp-workbench/WorkbenchUtil";
import DpInitialSettingModal from "./DpInitialSettingModal";
import { FS } from "@plannel/utils/font-util";

const DpVersionModal = props => {
  const { t, close, open, existVersion, selectVersion, reload, userRoles, running, aiConfirm } = props;
  // aiConfirm: AI 시나리오 확정용 모드 — { summary } (상단 배너 표시 + 초기값을 '최종 릴리즈 버전'으로 프리셋).
  //   생성 직후 호출측(AI 화면)이 수요 적재 → 하향/제외(apply-transform)를 적용한다.

  const today = new ZDate();
  const { module: { DP }, buckets, dayOfWeek, dpConfig, roles, dpInitalValues, dpSettingCode, dpVersion, isWeekBucket } = codeUtil;

  const { RELEASED: releaseStatus, READY: readyStatus } = dpVersion;
  const { INITIAL_DEMAND, AVG_FLG, BUCKET_UNIT, RUN_BF, START_DAY, NOTE_FLG, POS_ACTUAL_FLG, POS_AVG_FLG, POS_AVG_MONTHS } = dpConfig;
  const { LAST_ACTUAL_SALES, ACTUAL_SALES_YOY, LAST_RELEASED_VERSION, SALES_PLAN_MEASURE, FINANCE_PLAN, NOTINITIAL, CUSTOMIZATION, MEASURE_CUSTOM } = dpInitalValues;
  const { BASELINE_FORECAST, ITEM_LEVEL_ID, CUST_LEVEL_ID, ITEM_LEVEL, CUST_LEVEL, ROLLING_RANGE } = dpSettingCode;
  const { BF_QTY } = codeUtil.dpMeasure;
  const CUSTOMER = codeUtil.customer;
  const ITEM = codeUtil.item;

  const hasBaselineForecastSource = list => (list ?? []).some(compute => Object.values(compute).some(expr => expr?.includes(`[${BF_QTY.cd}]`)));

  const [prevVersion, setPrevVersion] = useState({ name: t("MSG_NoPreVersion"), startDate: today, endDate: today });
  const [configObj, setConfigObj] = useState({});
  const [horizonValues, setHorizonValues] = useState([]);
  const [versionList, setVersionList] = useState([]);

  const [curVerName, setCurVerName] = useState("");
  const [bucketUnit, setBucketUnit] = useState(buckets.MONTH);
  const timeDimensions = useRef(values(buckets));
  const [startDay, setStartDay] = useState(dayOfWeek.MONDAY.cd);
  const [oriMeasureList, setOriMeasureList] = useState([]);

  const [initialMeasureList, setInitialMeasureList] = useState([]);
  const [initialTarget, setInitialTarget] = useState([]);
  const [computeMeasureList, setComputeMeasureList] = useState([]);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [initialSettingOpen, setInitialSettingOpen] = useState(false);
  const [customInitialData, setCustomInitialData] = useState({});

  const [itemLevelCds, setItemLevelCds] = useState([]);
  const [custLevelCds, setCustLevelCds] = useState([]);
  const [itemLevelCd, setItemLevelCd] = useState([]);
  const [custLevelCd, setCustLevelCd] = useState([]);
  const [rollingRange, setRollingRange] = useState(0);

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });

  const [isError, setIsError] = useState(false);
  const [isLvError, setIsLvError] = useState(false);

  const [runBf, setRunBf] = useState(false);
  const [selectBfVersion, setSelectBfVersion] = useState("");
  const [useCopyBf, setUseCopyBf] = useState(false);
  const [bfVersionList, setBfVersionList] = useState([]);
  const [noteFlg, setNoteFlg] = useState(false);
  const [avgFlg, setAvgFlg] = useState(false);
  /* 신품목 / 구품목 수요 맵핑 */
  const [npiMappingFlg, setNpiMappingFlg] = useState(false);
  const [avgMonths, setAvgMonths] = useState(3);
  const [posAvgFlg, setPosAvgFlg] = useState(false);
  const [posAvgMonths, setPosAvgMonths] = useState(3);
  const [posActualFlg, setPosActualFlg] = useState(false);

  const moduleCd = DP;
  const demandValList = [LAST_ACTUAL_SALES, ACTUAL_SALES_YOY, LAST_RELEASED_VERSION, SALES_PLAN_MEASURE, FINANCE_PLAN, NOTINITIAL, CUSTOMIZATION];
  // [다음 릴리즈 배포] Measure Customization — 이번 릴리즈 제외
  // const demandValList = [LAST_ACTUAL_SALES, ACTUAL_SALES_YOY, LAST_RELEASED_VERSION, SALES_PLAN_MEASURE, FINANCE_PLAN, NOTINITIAL, CUSTOMIZATION, MEASURE_CUSTOM];
  const initialValues = includes(userRoles, roles.APP_BF) ? [BASELINE_FORECAST, ...demandValList] : demandValList;

  const [initialValue, setInitialValue] = useState(NOTINITIAL);
  const { snackBarMessageColor } = ColorUtil;

  const handleChange = event => {
    if (event.target.name === "initialValue") {
      setInitialValue(event.target.value);
      const defaultComputeList = WorkbenchUtil.getDefaultComputeList(event.target.value);
      setComputeMeasureList(defaultComputeList);
      if (event.target.value === BASELINE_FORECAST) {
        setCustLevelCd(custLevelCds[0] ?? CUSTOMER)
        setItemLevelCd(itemLevelCds[0] ?? ITEM)
      }
    } else if (event.target.name === CUST_LEVEL) {
      const cust = custLevelCds.filter(function (el) { return el.id === event.target.value})[0];
      setCustLevelCd(cust)
    } else if (event.target.name === ITEM_LEVEL) {
      const item = itemLevelCds.filter(function (el) { return el.id === event.target.value})[0];
      setItemLevelCd(item)
    }
  };

  const filterActionItem = params => {
    if (params.type === "click") {
      setFilterModalOpen(true);
    }
  };
  
  const clickInitialDetail = params => {
    if (params.type === "click") {
      setInitialSettingOpen(true);
    }
  };

  const handelFilterClose = () => {
    setFilterModalOpen(false);
  };

  const handelIntialSettingClose = () => {
    setInitialSettingOpen(false);
  };

  const createVersionName = () => {
    // AI 시나리오 확정 모드(aiConfirm)로 열렸을 때는 기본 버전명에 [AI] prefix 를 붙여
    // AI 시나리오로 만든 버전임을 구분한다 (사용자 수정 가능한 기본값일 뿐 강제 아님).
    const withAiPrefix = (n) => (aiConfirm ? `[AI] ${n}` : n);
    planVersionService.createVersionName({ moduleCd })
    .then(response => {
      setCurVerName(withAiPrefix(response.data))
    })
    .catch(e => {
      debug(e);
      setCurVerName(withAiPrefix(`${moduleCd}${dateUtils.getToday()}S001`))
    });
  };

  const setVersions = versions => {
    if (size(versions[0]) > 0) {
      const { name, planConfigObj } = versions[0];
      if (name && planConfigObj) {
        const { PLAN_START_DATE, PLANNING_RANGE, BUCKET_UNIT, START_DAY } = planConfigObj;
        setPrevVersion({
          name,
          startDate: new ZDate(PLAN_START_DATE),
          endDate: dateUtils.calculateRangeDate(PLAN_START_DATE, BUCKET_UNIT, START_DAY, PLANNING_RANGE, true)
        });
      }
    }
  };

  const setInitialValues = result => {
    if (!isEmpty(result)) {
      if (result.strVal) {
        setInitialValue(result.strVal);
      }
      for (const key of Object.keys(result.objVal)) {
        if (key === CUST_LEVEL_ID) {
          const cust = custLevelCds.filter(function (el) { return el.id === result.objVal[key]})[0];
          setCustLevelCd(cust)
        } else if (key === ITEM_LEVEL_ID) {
          const item = itemLevelCds.filter(function (el) { return el.id === result.objVal[key]})[0];
          setItemLevelCd(item)
        } else if (key === ROLLING_RANGE) {
          setRollingRange(result.objVal[key])
        } else {
          if (key !== "defaultValue" && key !== CUSTOMIZATION && key !== "copyTarget") {
            setComputeMeasureList(result.objVal[key])
          }
        }
      }
    }
  };

  const initialPlanConfig = () => {
    const param = {
      moduleCd: DP,
      activeFlg: true
    };

    planConfigService.getAll(param)
      .then(response => {
        const idObj = {};
        if (response.data?.results) {
          const resultData = response.data.results;
          for (const result of resultData) {
            if (isEqual(result.configCd, BUCKET_UNIT)) {
              setBucketUnit(result.strVal);
            } else if (isEqual(result.configCd, RUN_BF)) {
              setRunBf(result.boolVal);
            } else if (isEqual(result.configCd, START_DAY)) {
              setStartDay(result.strVal);
            } else if (isEqual(result.configCd, dpConfig[INITIAL_DEMAND])) {
              setCustomInitialData(result);
              if (result.objVal && Object.keys(result.objVal).length > 0) {
                setInitialValues(result);
              } else {
                if (!isEqual(result.strVal, CUSTOMIZATION)) {
                  const defaultComputeList = WorkbenchUtil.getDefaultComputeList(result.strVal);
                  setComputeMeasureList(defaultComputeList);
                }
              }
            } else if (isEqual(result.configCd, NOTE_FLG)) {
              setNoteFlg(result.boolVal);
            } else if (isEqual(result.configCd, AVG_FLG)) {
              setAvgFlg(result.boolVal);
              setAvgMonths(result.numVal);
              setPosAvgFlg(result.objVal?.POS_AVG_FLG ?? false);
            } else if (isEqual(result.configCd, POS_ACTUAL_FLG)) {
              setPosAvgMonths(result.numVal ?? 3);
              setPosActualFlg(result.boolVal);
            }
            idObj[result.configCd] = result;
          }
          setConfigObj(idObj);
        } else {
          setBucketUnit(bucketUnit);
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const retrievePlanVersion = () => {
    const statusCds = [releaseStatus, readyStatus];
    planVersionService.getAll({ moduleCd, statusCds,  pagination: false, orderByColumn: "releaseTs", sortType: "desc" })
      .then(response => {
        const { results } = response.data;
        if (!isEmpty(results)) {
          const preVersions = [];
          for (const result of results) {
            if (isEqual(readyStatus, result.statusCd)) {
              result.name = "";
              result.planConfigObj.PLAN_START_DATE = new ZDate(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0);
            } else if (isEqual(releaseStatus, result.statusCd)) {
              preVersions.push(result);
            }
          }
          setVersionList(results);
          setVersions(preVersions);
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const retrieveBfPlanVersion = () => {
    const statusCds = [releaseStatus];
    const moduleCd = DP;
    planVersionService.getStatusPlanVersion({ moduleCd, statusCds, isBf: true })
      .then(response => {
        const { results } = response.data;
        if (!isEmpty(results)) {
          setSelectBfVersion(results[0].id)
          setBfVersionList(results);
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const retrieveHrchyConfig = () => {
    hrchyConfigService.getAll({ searchFilters: { hrchyNum: codeUtil.hrchyNum.HRCHY } })
      .then(response => {
        const itemConfig = [];
        const custConfig = [];
        if (response.data?.results) {
          const result = response.data.results;
          result.sort((a,b) => {
            if (a.seqNum < b.seqNum) {
                return -1;
            }

            if (a.seqNum > b.seqNum) {
                return 1;
            }
            return 0;
          });
          for (const row of result) {
            if (row.itemHrchyFlg) {
              itemConfig.push(row);
            } else {
              custConfig.push(row);
            }
          }
          setItemLevelCds([...itemConfig, ITEM]);
          setCustLevelCds([...custConfig, CUSTOMER]);
        }
      })
      .catch(e => {
        debug(e);
      })
  };

  const retrieveMeasures = () => {
    measureService
      .getAll({ isBasic: true })
      .then(response => {
        if (response.data?.results) {
          setOriMeasureList(response.data.results);
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  // Compute Express Setting 의 Target Measure 목록 - 기본은 MF/SF, 채널 버전은 POS_QTY 도 target 으로 고를 수 있어야 한다.
  useEffect(() => {
    if (oriMeasureList.length > 0) {
      // v26.10: POS 미출시 — SHOW_POS 가 꺼져 있으면 POS_QTY 를 target 으로 내놓지 않는다
      const targets = oriMeasureList.filter(item => item.measureCd.includes("SF") || item.measureCd.includes("MF")
        || (codeUtil.SHOW_POS && item.measureCd === codeUtil.dpMeasure.POS_QTY.cd));
      setInitialTarget(targets);
    }
  }, [oriMeasureList]);

  const handleClose = () => {
    setUseCopyBf(false)
    setNoteFlg(false);
    close(false);
  };

  const handleCreate = () => {
    if (existVersion?.statusCd === dpVersion.SCENARIO) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_ScenarioExist") });
      return false;
    }

    if (initialValue === CUSTOMIZATION) {
      if (isEmpty(customInitialData?.objVal?.[CUSTOMIZATION])) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_NoDataByCause", { cause: t(CUSTOMIZATION), title: t("scenario") }) });
        return false;
      }
    }

    if (!curVerName) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_InputMandatory") });
      setIsError(true);
      return false;
    }
    if (useCopyBf && runBf) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_OnlySelectOneRunBfOrUseBfValue") });
      return false;
    }
    if (size(horizonValues) > 0) {
      const startDate = horizonValues[0];
      const endDate = horizonValues[1];
      const buckets = dateUtils.makeTimeBucket(startDate, endDate, bucketUnit, startDay);

      const planRange = buckets.length;
      let initial = {};
      if (initialValue === NOTINITIAL && initialValue) {
        initial = {[`${initialValue}`]: []}
      } else {
        initial = {[`${initialValue}`]: computeMeasureList}
      }

      if ((initialValue !== CUSTOMIZATION && initialValue !== NOTINITIAL) && size(computeMeasureList) < 1) {
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_ComputeSettingRequired") });
        return false;
      }

      const needsLevel = initialValue === BASELINE_FORECAST || (initialValue === MEASURE_CUSTOM && hasBaselineForecastSource(computeMeasureList));
      if (needsLevel) {
        if (isEmpty(custLevelCd) || isEmpty(itemLevelCd)) {
          setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_ConfirmLevel") });
          setIsLvError(true);
          return false;
        }
        initial = { ...initial, [CUST_LEVEL_ID]: custLevelCd ? custLevelCd.id : custLevelCds[0].id, [ITEM_LEVEL_ID]: itemLevelCd ? itemLevelCd.id : itemLevelCds[0].id }
      } else if (initialValue === CUSTOMIZATION) {
        initial = customInitialData?.objVal;
      }

      initial = { ...initial, [ROLLING_RANGE]: rollingRange }

      const planConfigObj = {
        PLAN_START_DATE: startDate,
        PLANNING_RANGE: planRange,
        BUCKET_UNIT: bucketUnit,
        START_DAY: startDay,
        INITIAL_DEMAND: initial,
        NOTE_FLG: noteFlg,
        AVG_FLG: avgFlg,
        NPI_MAPPING_FLG: npiMappingFlg,
        AVG_MONTHS: avgMonths,
        [POS_AVG_FLG]: posAvgFlg,
        [POS_AVG_MONTHS]: posAvgMonths,
        [POS_ACTUAL_FLG]: posActualFlg,
        DTF: configObj?.DTF?.numVal ?? 0
      }
      if (useCopyBf) {
        if (selectBfVersion === "") {
          setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_NoScenario") });
          return;
        }
        planConfigObj.BF_VERSION = selectBfVersion;
      }
      if (runBf) {
        planConfigObj.BF_AUTO = true;
      }
      const params = {
        moduleCd,
        name: curVerName,
        statusCd: readyStatus,
        planConfigObj
      };
      planVersionService.create(params)
        .then(response => {
          if (response.data.message) {
            running(false);
            if (response.data.message?.message) {
              setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t(response.data.message?.message) });
            } else {
              setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_CreateVersionFailed") });
            }
          } else {
            const createdVersion = response.data.data;
            if (createdVersion) {
              if (selectVersion) {
                if (runBf) {
                  createdVersion.bfAutoFlg = true;
                }
                if (useCopyBf) {
                  createdVersion.bfName = bfVersionList?.filter(bf => bf.id === selectBfVersion)[0]?.name;
                }
                selectVersion(createdVersion);
              }
              if (reload) {
                reload(true);
              }
            }
            running(true);
            handleClose();
          }
        })
        .catch(e => {
          running(false);
          setSnackInfo({ open: true, severity: snackBarMessageColor.networkError, content: t("MSG_CreateVersionFailed") });
          debug(e);
        });
    }
    return true;
  };

  const handleDialog = action => {
    if (action === "CREATE" || action === "WARNING") {
      handleCreate();
    }
  };

  const handleChangeSelect = event => {
    const { name: targetNm, value: targetVal } = event.target;
    if (targetNm === "startDay") {
      if (targetVal) {
        setStartDay(targetVal);
      }
    } else if (targetNm === "bucketUnit") {
      if (targetVal) {
        setBucketUnit(targetVal);
      }
    } else if (targetNm === "bfVersion") {
      setSelectBfVersion(targetVal)
    } else if (targetNm === "useBfValue") {
      setUseCopyBf(!useCopyBf)
    } else if (targetNm === "runBf") {
      setRunBf(!runBf);
    } else if (targetNm === "noteFlg") {
      setNoteFlg(!noteFlg);
    } else if (targetNm === "avgFlg") {
      setAvgFlg(!avgFlg);
    } else if (targetNm === "posAvgFlg") {
      setPosAvgFlg(!posAvgFlg);
    } else if (targetNm === "npiMappingFlg") {
      setNpiMappingFlg(!npiMappingFlg);
    }
  };

  const onChangeSearchCode = e => {
    const currentVersionName = e.target.value;
    setCurVerName(currentVersionName);
  };

  useEffect(() => {
    if (open) {
      retrieveHrchyConfig();
      createVersionName();
      retrievePlanVersion();
      retrieveBfPlanVersion();
      retrieveMeasures();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // AI 확정 모드 — 초기값을 '최종 릴리즈 버전'으로 프리셋(빈 값이면 하향 대상이 없음).
  useEffect(() => {
    if (open && aiConfirm) {
      setInitialValue(LAST_RELEASED_VERSION);
      setComputeMeasureList(WorkbenchUtil.getDefaultComputeList(LAST_RELEASED_VERSION));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!isEmpty(itemLevelCds)) {
      initialPlanConfig();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemLevelCds]);

  useEffect(() => {
    if (curVerName) {
      setIsError(false);
    }
  }, [curVerName]);

  useEffect(() => {
    if (oriMeasureList.length > 0) {
      const initialMeasures = codeUtil.getSourceMeasureList(oriMeasureList, initialValue, posActualFlg);
      setInitialMeasureList(initialMeasures);
    }
    // 최종 릴리즈 버전이 아니면 신품목/구품목 수요 맵핑은 대상이 아니라 체크를 내린다
    if (!isEqual(initialValue, LAST_RELEASED_VERSION) || rollingRange <= 0) {
      setNpiMappingFlg(false);
    }
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue, posActualFlg]);

  useEffect(() => {
    if (initialValue === MEASURE_CUSTOM && hasBaselineForecastSource(computeMeasureList)) {
      setCustLevelCd(prev => (isEmpty(prev) ? (custLevelCds[0] ?? CUSTOMER) : prev));
      setItemLevelCd(prev => (isEmpty(prev) ? (itemLevelCds[0] ?? ITEM) : prev));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue, computeMeasureList]);

  useEffect(() => {
    if (!isEmpty(customInitialData)) {
      setInitialValues(customInitialData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customInitialData]);

  return (
    <>
      {/* 아이콘은 PlayArrow — 저장이 아니라 생성이다. DP Process Mgmt 의 "Start DP" 버튼과 맞춘 것.
          주의: 이 프로젝트의 RunIcon 도 PlayArrow 라 Run 모달의 실행 버튼과 같은 아이콘이 된다. */}
      <BaseConfigModal
        open={open}
        onClose={handleClose}
        title={t("createNewVersion")}
        saveLabel={t("create")}
        saveIcon={<PlayArrowIcon />}
        onSave={() => {
          if ((initialValue !== CUSTOMIZATION && initialValue !== NOTINITIAL) && size(computeMeasureList) < 1) {
            setSnackInfo({ open: true, severity: "error", content: t("MSG_ComputeSettingRequired") });
            return;
          }
          setDialogInfo({ open: true, title: "create", content: t("MSG_CreateVersionConfirm") });
        }}
        width={452}>
          {aiConfirm?.summary && (
            <Box sx={{ mb: 1, mx: 0.5, p: 1, borderRadius: 1, bgcolor: "rgba(24,95,165,0.07)",
                       border: "0.5px solid rgba(24,95,165,0.35)" }}>
              <Typography sx={{ fontSize: FS.SUB, fontWeight: 700, color: "primary.main" }}>
                AI 적용 파라미터 (버전 생성 후 자동 적용)
              </Typography>
              <Typography sx={{ fontSize: FS.TITLE, color: "#222" }}>{aiConfirm.summary}</Typography>
              <Typography sx={{ fontSize: FS.BODY, color: "#666", mt: 0.3 }}>
                ※ 초기값은 '{t(LAST_RELEASED_VERSION)}'로 설정됩니다(하향 대상이 되는 현행 계획 복사).
              </Typography>
            </Box>
          )}
          <AppCard variant="outlined">
            <CardContent sx={{ display: "flex", pb: 0 }}>
              <Box
                size="small"
                sx={{ width: "100%" }}>
                <Box
                  size="small"
                  sx={{ gap: 1, display: "flex", textAlign: "center" }}>
                  <FormControl
                    size="small"
                    style={{ width: "100%" }}>
                    <TextField
                      label={t("prevVersion")}
                      size="small"
                      value={prevVersion.name}
                      type="string"
                      sx={{ textAlign: "center" }}
                      readOnly
                      disabled
                    />
                  </FormControl>
                </Box>
                <Box
                  size="small"
                  sx={{ textAlign: "left", width: "100%" }}>
                  <Typography
                    id="modal-modal-title"
                    variant="body1"
                    sx={{ mt: 1, mb: 0.5, ml: 1 }}>
                    {t("sourceData")}
                  </Typography>
                  <Box size="small">
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <FormControl
                        size="small"
                        style={{ width: "48%" }}>
                        <DatePicker
                          label={isWeekBucket(bucketUnit) ? t("planStartDate") : t("planStartMonth")}
                          format={isWeekBucket(bucketUnit) ? "yyyy-MM-dd" : "yyyy-MM"}
                          size="small"
                          sx={{ mt: 1, height: "40px" }}
                          value={prevVersion.startDate ? prevVersion.startDate : today}
                          readOnly
                          disabled
                          onAccept={() => {
                            /** empty */
                          }}
                          slotProps={{ textField: { size: "small" } }}
                        />
                      </FormControl>
                      <FormControl
                        size="small"
                        style={{ width: "48%" }}
                        sx={{ ml: 1 }}>
                        <DatePicker
                          label={isWeekBucket(bucketUnit) ? t("planEndDate") : t("planEndMonth")}
                          format={isWeekBucket(bucketUnit) ? "yyyy-MM-dd" : "yyyy-MM"}
                          size="small"
                          sx={{ mt: 1, height: "40px" }}
                          value={prevVersion.endDate ? prevVersion.endDate : today}
                          readOnly
                          disabled
                          onAccept={() => {
                            /** empty */
                          }}
                          slotProps={{ textField: { size: "small" } }}
                        />
                      </FormControl>
                    </LocalizationProvider>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </AppCard>
          <AppCard
            variant="outlined"
            sx={{ mt: 1 }}>
            <CardContent sx={{ display: "flex", pb: 0 }}>
              <Box
                size="small"
                sx={{ width: "100%" }}>
                <Box
                  size="small"
                  sx={{ mt: 1, gap: 1, display: "flex", textAlign: "center" }}>
                  <FormControl
                    size="small"
                    style={{ width: "100%" }}>
                    <TextField
                      label={t("versionName")}
                      size="small"
                      value={curVerName}
                      type="string"
                      sx={{ textAlign: "center" }}
                      onChange={onChangeSearchCode}
                      error={isError}
                    />
                  </FormControl>
                </Box>
                <Box
                  size="small"
                  sx={{ textAlign: "left", width: "100%" }}>
                  <Box
                    size="small"
                    sx={{ display: "flex", mt: 2 }}>
                    <PlanHorizonSelect
                      selectSize={"small"}
                      currentYN={false}
                      hasDTF={false}
                      configObj={configObj}
                      getValues={params => setHorizonValues(params)}
                    />
                  </Box>
                  <Box
                    size="small"
                    sx={{ mt: 2 }}>
                    <FormControl
                      size="small"
                      sx={{ width: isWeekBucket(bucketUnit) ? "49.5%" : "100%" }}>
                      <InputLabel>{t("planningBucket")}</InputLabel>
                      <Select
                        size="small"
                        variant="outlined"
                        name="bucketUnit"
                        label="Time Dimension"
                        value={bucketUnit}
                        onChange={event => {
                          handleChangeSelect(event);
                        }}
                        disabled>
                        {timeDimensions.current.length > 0 &&
                          timeDimensions.current.map(level => {
                            return (
                              <MenuItem
                                value={level}
                                key={level}>
                                {t(level)}
                              </MenuItem>
                            );
                          })}
                      </Select>
                    </FormControl>
                    {isWeekBucket(bucketUnit) ?
                      <FormControl
                        size="small"
                        sx={{ width: "49.5%", ml: "1%" }}>
                        <InputLabel>{t("dayOfWeek")}</InputLabel>
                        <Select
                          size="small"
                          variant="outlined"
                          name="startDay"
                          label={t("dayOfWeek")}
                          value={startDay}
                          onChange={event => {
                            handleChangeSelect(event);
                          }}
                          disabled>
                          {Object.values(dayOfWeek).length > 0 &&
                            Object.values(dayOfWeek).map(day => {
                              return (
                                <MenuItem
                                  value={day.cd}
                                  key={day.name}>
                                  {day.name}
                                </MenuItem>
                              );
                            })}
                        </Select>
                      </FormControl> : <Box></Box>}
                  </Box>
                  <WorkbenchUtil.MeasureFormulaBox
                    computeMeasureList={computeMeasureList}
                    oriMeasureList={oriMeasureList}
                  />
                  <Box
                    size="small"
                    sx={{ mt: 2 }}>
                    <FormControl
                      size="small"
                      sx={{ width: "90%" }}>
                      <InputLabel>{t("createDefaultValue")}</InputLabel>
                      <Select
                        size="small"
                        variant="outlined"
                        name="initialValue"
                        label={t("createDefaultValue")}
                        value={initialValue}
                        onChange={event => {
                          handleChange(event);
                        }}>
                        {initialValues?.map(initalValue => {
                            return (
                              <MenuItem
                                key={initalValue}
                                value={initalValue}>
                                {t(initalValue)}
                              </MenuItem>
                            );
                          })}
                      </Select>
                    </FormControl>
                    {initialValue !== CUSTOMIZATION ? (
                      <CalculationButton
                        sx={{ width: "10%" }}
                        label="computeExpressSetting"
                        color={computeMeasureList.length > 0 ? "primary" : "black"}
                        disabled={initialValue === NOTINITIAL}
                        onClick={filterActionItem} />
                    ) : null}
                     {initialValue === CUSTOMIZATION ? (
                        <DetailButton
                          sx={{ width: "10%" }}
                          label="initialPlanDetailSetting"
                          color={"primary"}
                          onClick={clickInitialDetail}
                        />
                        ) : null}
                  </Box>
                  {initialValue === BASELINE_FORECAST || (initialValue === MEASURE_CUSTOM && hasBaselineForecastSource(computeMeasureList)) ?
                <Box sx={{ mt: 1 }}  >
                  <FormControl size="small" sx={{ width: "44%", mr: "1%" }}>
                    <InputLabel>{t(CUST_LEVEL)}</InputLabel>
                    <Select
                      id={CUST_LEVEL}
                      variant="outlined"
                      name={CUST_LEVEL}
                      value={!isEmpty(custLevelCd) ? custLevelCd?.id : custLevelCds ? custLevelCds[0]?.id : CUSTOMER.id}
                      label={t(CUST_LEVEL)}
                      error={isLvError}
                      onChange={event => {
                        handleChange(event);
                      }}>
                      {custLevelCds?.map(custLevel => {
                        return (
                          <MenuItem
                            key={custLevel.id}
                            value={custLevel.id}
                          >
                            {custLevel.name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                  <FormControl size="small" sx={{ width: "45%" }}>
                    <InputLabel>{t(ITEM_LEVEL)}</InputLabel>
                    <Select
                      id={ITEM_LEVEL}
                      variant="outlined"
                      name={ITEM_LEVEL}
                      value={!isEmpty(itemLevelCd) ? itemLevelCd?.id : itemLevelCds ? itemLevelCds[0]?.id : ITEM.id}
                      label={t(ITEM_LEVEL)}
                      error={isLvError}
                      onChange={event => {
                        handleChange(event);
                      }}>
                      {itemLevelCds?.map(itemLevel => {
                        return (
                          <MenuItem
                            key={itemLevel.id}
                            value={itemLevel.id}
                          >
                            {itemLevel.name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Box> : <Box></Box>}
                  {includes(userRoles, roles.APP_BF) ? (
                    <Box>
                      <FormControlLabel
                        sx={{width: "100%"}}
                        control={
                        <Checkbox
                          name="runBf"
                          checked={runBf}
                          disabled={useCopyBf}
                          onChange={event => handleChangeSelect(event)} />}
                          label={t("runBf")}
                      />
                      <FormControlLabel
                        sx={{width: "100%"}}
                        control={
                          <Checkbox
                            name="useBfValue"
                            checked={useCopyBf}
                            disabled={runBf}
                            onChange={event => handleChangeSelect(event)}
                          />
                        }
                        label={t("useBfValue")}
                      />
                      {useCopyBf && (
                        <FormControl size="small" sx={{ width: "100%", ml: "1%", mt: 1 }}>
                          <InputLabel>{t("baseForecastVersion")}</InputLabel>
                          <Select
                            size="small"
                            variant="outlined"
                            name="bfVersion"
                            label={t("baseForecastVersion")}
                            value={selectBfVersion}
                            onChange={event => handleChangeSelect(event)}
                          >
                            {bfVersionList?.length > 0 &&
                              bfVersionList.map(ver => (
                                <MenuItem
                                  value={ver?.id}
                                  key={ver?.name}>
                                  {ver?.name}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      )}
                    </Box>
                  ) : (
                    <Box />
                  )}
                  <Box>
                    <FormControlLabel
                      control={<Checkbox color="primary" name={"noteFlg"} checked={noteFlg} onChange={event => handleChangeSelect(event)} />}
                      label={t("noteCopy")} key="NOTE_FLG" sx={{ width: "100%" }}
                    />
                  </Box>
                  {/* 이 체크박스만 mt: 1 짜리 래퍼 Box 를 하나 더 두르고 있어서 앞의 셋보다 8px 더 떨어졌다. */}
                  <Box>
                    <FormControlLabel
                      control={<Checkbox color="primary" name={"avgFlg"} checked={avgFlg}
                                         onChange={param => handleChangeSelect(param)}/>}
                      label={t("avgNoOfMonths", { N: avgMonths, measureName: t("avgSalesActual") })} key="AVG_FLG" sx={{ width: "100%" }}
                    />
                  </Box>
                  {codeUtil.SHOW_POS && (
                  <Box>
                    <FormControlLabel
                      control={<Checkbox color="primary" name={"posAvgFlg"} checked={posAvgFlg}
                                         onChange={param => handleChangeSelect(param)}/>}
                      label={t("avgNoOfMonths", { N: posAvgMonths, measureName: t("posAvgSalesActual") })} key="POS_AVG_FLG" sx={{ width: "100%" }}
                    />
                  </Box>
                  )}
                  {/* 신품목/구품목 수요 맵핑은 수요계획 초기값이 최종 릴리즈 버전일 때만 쓸 수 있다 */}
                  {isEqual(initialValue, LAST_RELEASED_VERSION) || rollingRange > 0 ? (
                    <Box>
                      <FormControlLabel
                        control={<Checkbox color="primary" name={"npiMappingFlg"} checked={npiMappingFlg}
                                           onChange={param => handleChangeSelect(param)}/>}
                        label={t("npiDemandMapping")} key="NPI_MAPPING_FLG" sx={{ width: "100%" }}
                      />
                    </Box>
                  ) : null}
                </Box>
              </Box>
            </CardContent>
          </AppCard>
      </BaseConfigModal>
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        actionName={dialogInfo.actionName}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
        <ComputeMeasureModal
        open={filterModalOpen}
        close={handelFilterClose}
        modalResultData={setComputeMeasureList}
        target={initialTarget.length > 0 ? initialTarget : oriMeasureList}
        source={initialMeasureList.length > 0 ? initialMeasureList : oriMeasureList}
        computeList = {computeMeasureList}
      />
      <DpInitialSettingModal
        open={initialSettingOpen}
        close={handelIntialSettingClose}
        itemLevelCds={itemLevelCds}
        custLevelCds={custLevelCds}
        initialValues={initialValues}
        oriMeasureList={oriMeasureList}
        configDatas={customInitialData}
        modalResultData={setCustomInitialData}
        versionList={versionList}
        posActualFlg={posActualFlg}
      />
    </>
  );
};

const DpVersionSchedule = props => {
  const { t } = useTranslation();
  const { planConfigResult, oriMeasureList, initialTarget, jobInfo, jobInfoSettingRef, hrchyConfig, userRoles } = props;
  const { module: { DP }, buckets, dayOfWeek, dpConfig, roles, dpInitalValues, dpSettingCode, isWeekBucket } = codeUtil;
  const { QUARTER } = buckets;
  const { PLANNING_RANGE, BUCKET_UNIT, START_DAY, INITIAL_DEMAND, RUN_BF, NOTE_FLG, AVG_FLG, BF_VERSION, AVG_MONTHS, NPI_MAPPING_FLG, POS_ACTUAL_FLG, POS_AVG_FLG, POS_AVG_MONTHS }= dpConfig;
  const { LAST_ACTUAL_SALES, ACTUAL_SALES_YOY, LAST_RELEASED_VERSION, SALES_PLAN_MEASURE, FINANCE_PLAN, NOTINITIAL, CUSTOMIZATION, MEASURE_CUSTOM } = dpInitalValues;
  const { BASELINE_FORECAST, ITEM_LEVEL_ID, CUST_LEVEL_ID, CUST_LEVEL, ITEM_LEVEL, ROLLING_RANGE } = dpSettingCode;
  const { BF_QTY } = codeUtil.dpMeasure;
  const CUSTOMER = codeUtil.customer;
  const ITEM = codeUtil.item;
  const hasBaselineForecastSource = list => (list ?? []).some(compute => Object.values(compute).some(expr => expr?.includes(`[${BF_QTY.cd}]`)));

  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [planningRange, setPlanningRange] = useState(jobInfo?.planningRange ? jobInfo?.planningRange : 12);
  const [fromDate, setFromDate] = useState(new ZDate());
  const [toDate, setToDate] = useState(new ZDate());

  const [bucketUnit, setBucketUnit] = useState(buckets.MONTH);
  const timeDimensions = useRef(values(buckets));
  const [startDay, setStartDay] = useState(jobInfo?.startDay || dayOfWeek.MONDAY.cd);

  const [initialMeasureList, setInitialMeasureList] = useState([]);
  const [computeMeasureList, setComputeMeasureList] = useState([]);
  const [filterModalOpen, setFilterModalOpen] = useState(false);
  const [itemLevelCds, setItemLevelCds] = useState([]);
  const [custLevelCds, setCustLevelCds] = useState([]);
  const [itemLevelCd, setItemLevelCd] = useState({});
  const [custLevelCd, setCustLevelCd] = useState({});
  const [rollingRange, setRollingRange] = useState(0);
  const [runBf, setRunBf] = useState(jobInfo?.[RUN_BF]);
  const [customInitialData, setCustomInitialData] = useState({});
  const [initialSettingOpen, setInitialSettingOpen] = useState(false);
  const [selectBfVersion, setSelectBfVersion] = useState(jobInfo?.BF_VERSION);
  const [useCopyBf, setUseCopyBf] = useState(jobInfo?.BF_VERSION !== null && jobInfo?.BF_VERSION);
  const [bfVersionList, setBfVersionList] = useState([]);
  const [noteFlg, setNoteFlg] = useState(false);
  /* 신품목 / 구품목 수요 맵핑 */
  const [npiMappingFlg, setNpiMappingFlg] = useState(false);
  const [avgFlg, setAvgFlg] = useState(false);
  const [avgMonths, setAvgMonths] = useState(3);
  const [posAvgFlg, setPosAvgFlg] = useState(false);
  const [posAvgMonths, setPosAvgMonths] = useState(3);
  const [posActualFlg, setPosActualFlg] = useState(false);

  const moduleCd = DP;
  const { snackBarMessageColor } = ColorUtil;
  const demandValList = [LAST_ACTUAL_SALES, ACTUAL_SALES_YOY, LAST_RELEASED_VERSION, SALES_PLAN_MEASURE, FINANCE_PLAN, NOTINITIAL, CUSTOMIZATION];
  // [다음 릴리즈 배포] Measure Customization — 이번 릴리즈 제외
  // const demandValList = [LAST_ACTUAL_SALES, ACTUAL_SALES_YOY, LAST_RELEASED_VERSION, SALES_PLAN_MEASURE, FINANCE_PLAN, NOTINITIAL, CUSTOMIZATION, MEASURE_CUSTOM];
  const initialValues = includes(userRoles, roles.APP_BF) ? [BASELINE_FORECAST, ...demandValList] : demandValList;

  const [initialValue, setInitialValue] = useState(NOTINITIAL);

  const handleChange = event => {
    if (event.target.name === "initialValue") {
      setInitialValue(event.target.value);
      const defaultComputeList = WorkbenchUtil.getDefaultComputeList(event.target.value);
      setComputeMeasureList(defaultComputeList);
      if (event.target.value === BASELINE_FORECAST) {
        setCustLevelCd(custLevelCds[0] ?? CUSTOMER)
        setItemLevelCd(itemLevelCds[0] ?? ITEM)
      }
    } else if (event.target.name === CUST_LEVEL) {
      const cust = custLevelCds.filter(function (el) { return el.id === event.target.value})[0];
      setCustLevelCd(cust);
    } else if (event.target.name === ITEM_LEVEL) {
      const item = itemLevelCds.filter(function (el) { return el.id === event.target.value})[0];
      setItemLevelCd(item);
    }
  };

  const handleBlur = () => {
    if (planningRange === "") {
      setPlanningRange(12);
    } 
  };

  const filterActionItem = params => {
    if (params.type === "click") {
      setFilterModalOpen(true);
    }
  };

  const handelFilterClose = () => {
    setFilterModalOpen(false);
  };

  const retrieveBfPlanVersion = () => {
    const statusCds = [codeUtil.dpVersion.RELEASED];
    const moduleCd = DP;
    planVersionService.getStatusPlanVersion({ moduleCd, statusCds, isBf: true })
      .then(response => {
        const { results } = response.data;
        if (!isEmpty(results)) {
          if (jobInfo?.BF_VERSION !== "") {
            const bfVersion = results.find(bfVersion => bfVersion.id === jobInfo?.BF_VERSION);
            if (bfVersion) {
              setSelectBfVersion(jobInfo?.BF_VERSION)
              setUseCopyBf(true)
            } else {
              setSelectBfVersion(results[0].id)
            }
          } else {
            setSelectBfVersion(results[0].id)
          }
          setBfVersionList(results);
        }
      })
      .catch(e => {
        debug(e);
      });
  };

  const isObject = (value) => {
    return value !== null && typeof value === "object";
  };

  const initialPlanConfig = () => {
    if (planConfigResult) {
      for (const result of planConfigResult) {
        if (isEqual(result.configCd, BUCKET_UNIT)) {
          setBucketUnit(jobInfo?.[BUCKET_UNIT] || result.strVal);
        } else if (isEqual(result.configCd, RUN_BF)) {
          setRunBf(jobInfo?.[RUN_BF]);
        } else if (isEqual(result.configCd, INITIAL_DEMAND)) {
          const initialDemand = jobInfo?.[INITIAL_DEMAND];
          //initial demand가 object이거나 string일 때 확인
          if (initialDemand && isObject(initialDemand)) {
            for (const key of Object.keys(initialDemand)) {
              if (key === CUST_LEVEL_ID) {
                const custId = initialDemand?.[CUST_LEVEL_ID];
                const cust = custLevelCds?.find(el => el.id === custId);
                setCustLevelCd(cust || {})
              } else if (key === ITEM_LEVEL_ID) {
                const itemId = initialDemand?.[ITEM_LEVEL_ID];
                const item = itemLevelCds?.find(el => el.id === itemId);
                setItemLevelCd(item || {})
              } else if (key === ROLLING_RANGE) {
                setRollingRange(initialDemand[key])
              } else {
                if (isObject(initialDemand[key])) {
                  setInitialValue(key);
                  setComputeMeasureList(initialDemand[key]);
                }
              }
            }
          } else if (initialDemand && typeof value === "string") {
              setInitialValue(initialDemand);
          } else {
              setInitialValue(result?.strVal);
          }
          if (result.strVal === CUSTOMIZATION) {
            setCustomInitialData(jobInfo?.[INITIAL_DEMAND] ? { strVal: CUSTOMIZATION, objVal: jobInfo?.[INITIAL_DEMAND] } : result);
            if (result?.objVal && Object.keys(result?.objVal).length > 0) {
              for (const key of Object.keys(result.objVal)) {
                if (key === CUST_LEVEL_ID) {
                  const custId = initialDemand?.[CUST_LEVEL_ID] || result.objVal[key];
                  const cust = custLevelCds.find(el => el.id === custId);
                  setCustLevelCd(cust)
                } else if (key === ITEM_LEVEL_ID) {
                  const itemId = initialDemand?.[ITEM_LEVEL_ID] || result.objVal[key];
                  const item = itemLevelCds.find(el => el.id === itemId);
                  setItemLevelCd(item)
                } else if (key === ROLLING_RANGE) {
                  setRollingRange(result.objVal[key])
                } else {
                  if (key !== "defaultValue" && key !== CUSTOMIZATION && key !== "copyTarget") {
                    const initialValue = !isEmpty(initialDemand)
                      ? Object.keys(initialDemand).find(key => Object.values({...dpInitalValues, BASELINE_FORECAST: "baselineForecast"}).includes(key)) || key
                      : key;
                    const computeMeasure = !isEmpty(initialDemand) ? initialDemand?.[initialValue] : result.objVal[key];
                    setComputeMeasureList(computeMeasure);
                    if (initialDemand?.[CUST_LEVEL_ID]) {
                      const custId = initialDemand?.[CUST_LEVEL_ID];
                      const cust = custLevelCds.find(el => el.id === custId);
                      setCustLevelCd(cust || {})
                    }
                    if (initialDemand?.[ITEM_LEVEL_ID]) {
                      const itemId = initialDemand?.[ITEM_LEVEL_ID];
                      const item = itemLevelCds.find(el => el.id === itemId);
                      setItemLevelCd(item || {})
                    }
                  }
                }
              }
            } else {
              if (!isEqual(result.strVal, CUSTOMIZATION)) {
                const initialValue = !isEmpty(initialDemand) ? Object.keys(initialDemand).find(key => Object.values(dpInitalValues).includes(key)) : Object.keys(result?.objVal)[0];
                const defaultComputeList = WorkbenchUtil.getDefaultComputeList(initialValue);
                setComputeMeasureList(defaultComputeList);
              }
            }
          }
        } else if (isEqual(result.configCd, PLANNING_RANGE)) {
          setPlanningRange(jobInfo?.[PLANNING_RANGE] || result.numVal);
        } else if (isEqual(result.configCd, NOTE_FLG)) {
          setNoteFlg(jobInfo?.[NOTE_FLG] || result?.boolVal);
        } else if (isEqual(result.configCd, AVG_FLG)) {
          setAvgFlg(jobInfo?.[AVG_FLG] || result?.boolVal);
          setAvgMonths(result?.numVal || 3);
          setPosAvgFlg(jobInfo?.[POS_AVG_FLG] || (result?.objVal?.POS_AVG_FLG ?? false));
        } else if (isEqual(result.configCd, POS_ACTUAL_FLG)) {
          setPosAvgMonths(result?.numVal || 3);
          setPosActualFlg(result?.boolVal);
        } else if (isEqual(result.confingCd, BF_VERSION)) {
          setSelectBfVersion(jobInfo?.BF_VERSION)
          setUseCopyBf(true)
        }
      }
    }
  };

  const jobInfoHandle = () => {
    let initial = {[`${initialValue}`]: computeMeasureList, [ROLLING_RANGE]: rollingRange};
    if (initialValue === NOTINITIAL) {
      initial = {[`${initialValue}`]: []}
    }

    const needsLevel = initialValue === BASELINE_FORECAST || (initialValue === MEASURE_CUSTOM && hasBaselineForecastSource(computeMeasureList));
    if (needsLevel && custLevelCd) {
      initial = {...initial, [CUST_LEVEL_ID]:custLevelCd?.id}
    }
    if (needsLevel && itemLevelCd) {
      initial = {...initial, [ITEM_LEVEL_ID]:itemLevelCd?.id}
    }

    if (initialValue === CUSTOMIZATION) {
      initial = customInitialData?.objVal;
    }

    const params = {
      moduleCd,
      [PLANNING_RANGE]: planningRange,
      [BUCKET_UNIT]: bucketUnit,
      [START_DAY]: startDay,
      [INITIAL_DEMAND]: initial,
      [RUN_BF]: runBf,
      [NOTE_FLG]: noteFlg,
      [AVG_FLG]: avgFlg,
      [NPI_MAPPING_FLG]: npiMappingFlg,
      [AVG_MONTHS]: avgMonths,
      [POS_AVG_FLG]: posAvgFlg,
      [POS_AVG_MONTHS]: posAvgMonths,
      [POS_ACTUAL_FLG]: posActualFlg
    };
    if (useCopyBf) {
      params.BF_VERSION = selectBfVersion;
    }
    jobInfoSettingRef.current = params;
  };

  const planningRangeInputHandle = (event) => {
    const inputValue = event.target.value;

    if (inputValue === "") {
      setPlanningRange("");
      return;
    }

    // 숫자만으로 이루어진 문자열인지 확인
    if (/^\d*$/.test(inputValue)) {
      const numberValue = Number(inputValue);

      if (numberValue >= 1 && numberValue <= 120) {
        setPlanningRange(numberValue);
      } else {
        setPlanningRange("");
        setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_EnterIntBetween", { min: 1, max: 120 })});
      }
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_EnterIntBetween", { min: 1, max: 120 })});
    }
  };

  const handleChangeSelect = event => {
    const { name: targetNm, value: targetVal } = event.target;
    if (targetNm === "startDay") {
      if (targetVal) {
        setStartDay(targetVal);
      }
    } else if (targetNm === "bucketUnit") {
      if (targetVal) {
        setBucketUnit(targetVal);
      }
    } else if (targetNm === "bfVersion") {
      setSelectBfVersion(targetVal)
    } else if (targetNm === "useBfValue") {
      setUseCopyBf(!useCopyBf)
    } else if (targetNm === "runBf") {
      setRunBf(!runBf);
    } else if (targetNm === "noteFlg") {
      setNoteFlg(!noteFlg);
    } else if (targetNm === "avgFlg") {
      setAvgFlg(!avgFlg);
    } else if (targetNm === "posAvgFlg") {
      setPosAvgFlg(!posAvgFlg);
    } else if (targetNm === "npiMappingFlg") {
      setNpiMappingFlg(!npiMappingFlg);
    }
  };

  const handleHrchyConfig = () => {
    const itemConfig = [];
    const custConfig = [];
    for (const row of hrchyConfig) {
      if (row.itemHrchyFlg) {
        itemConfig.push(row);
      } else {
        custConfig.push(row);
      }
    }
    setItemLevelCds([...itemConfig, ITEM]);
    setCustLevelCds([...custConfig, CUSTOMER]);
  };

  const clickInitialDetail = params => {
    if (params.type === "click") {
      setInitialSettingOpen(true);
    }
  };

  const handelIntialSettingClose = () => {
    setInitialSettingOpen(false);
  };

  useEffect(() => {
    if (oriMeasureList.length > 0) {
      const initialMeasures = codeUtil.getSourceMeasureList(oriMeasureList, initialValue, posActualFlg);
      setInitialMeasureList(initialMeasures);
    }
    // 최종 릴리즈 버전이 아니면 신품목/구품목 수요 맵핑은 대상이 아니라 체크를 내린다
    if (!isEqual(initialValue, LAST_RELEASED_VERSION) || rollingRange <= 0) {
      setNpiMappingFlg(false);
    }
     // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialValue, posActualFlg]);

  useEffect(() => {
    jobInfoHandle();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucketUnit, startDay, initialValue, custLevelCd, itemLevelCd, planningRange, runBf, useCopyBf, computeMeasureList, customInitialData, avgFlg, posAvgFlg, npiMappingFlg, noteFlg, selectBfVersion]);

  useEffect(() => {
    if (!isEmpty(planConfigResult)) {
      initialPlanConfig();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planConfigResult]);

  useEffect(() => {
    if (!isEmpty(hrchyConfig)) {
      handleHrchyConfig();
      retrieveBfPlanVersion();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hrchyConfig]);

  useEffect(() => {
    let calcFromDate = fromDate;
    if (isWeekBucket(bucketUnit)) {
      calcFromDate = dateUtils.nearDay(fromDate, startDay, bucketUnit);
      setFromDate(calcFromDate);
    }

    setToDate(dateUtils.calculateRangeDate(calcFromDate, bucketUnit, startDay, planningRange, true));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bucketUnit, planningRange, startDay]);

  return (
    <Box
      aria-labelledby="modal-modal-title"
      aria-describedby="modal-modal-description">
      <Box sx={{ width: "95%", height: "100%", p: 0.2 }}>
        <Box sx={{ display: "flex", pb: 0 }}>
          <Box
            size="small"
            sx={{ width: "100%", mt: 1 }}>
            <Box
              size="small"
              sx={{ textAlign: "left", width: "100%", ...PANEL_FORM_SX }}>
              {/* 컨트롤에 붙어 있던 이름표(InputLabel / label)를 전부 왼쪽 거터로 옮겼다.
                  Job Schedule 화면의 다른 Job Target 패널(Inventory Plan 등)과 같은 구조다. */}
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <PanelField label={isWeekBucket(bucketUnit) ? t("planStartDate") : t("planStartMonth")}>
                  <DatePicker
                    format={isWeekBucket(bucketUnit) ? "yyyy-MM-dd" : "yyyy-MM"}
                    value={fromDate}
                    onAccept={date => setFromDate(date)}
                    disabled
                    slotProps={{ textField: { size: "small", sx: { width: 180 } } }}
                  />
                </PanelField>
                <PanelField label={isWeekBucket(bucketUnit) ? t("planEndDate") : t("planEndMonth")}>
                  <DatePicker
                    format={isWeekBucket(bucketUnit) ? "yyyy-MM-dd" : "yyyy-MM"}
                    views={isWeekBucket(bucketUnit) ? ["year", "month", "day"] : ["year", "month"]}
                    value={toDate}
                    onAccept={date => setToDate(date)}
                    disabled
                    slotProps={{ textField: { size: "small", sx: { width: 180 } } }}
                  />
                </PanelField>
              </LocalizationProvider>
              <PanelField label={t("planRange")}>
                <TextField
                  size="small"
                  color="info"
                  sx={{ width: 120 }}
                  value={planningRange}
                  onChange={planningRangeInputHandle}
                  onBlur={handleBlur}
                  inputProps={{ "aria-label": t("planRange") }}
                />
              </PanelField>
              <PanelField label={t("planningBucket")}>
                <FormControl
                  size="small"
                  sx={{ width: 180 }}>
                  <Select
                    size="small"
                    variant="outlined"
                    name="bucketUnit"
                    value={bucketUnit}
                    inputProps={{ "aria-label": t("planningBucket") }}
                    onChange={event => {
                      handleChangeSelect(event);
                    }}
                    disabled>
                    {timeDimensions.current.length > 0 &&
                      timeDimensions.current.map(level => {
                        if (!isEqual(level, QUARTER)) {
                          return (
                            <MenuItem
                              value={level}
                              key={level}>
                              {t(level)}
                            </MenuItem>
                          )
                        }
                        return "";
                      })}
                  </Select>
                </FormControl>
              </PanelField>
              {isWeekBucket(bucketUnit) ?
                <PanelField label={t("dayOfWeek")}>
                  <FormControl
                    size="small"
                    sx={{ width: 180 }}>
                    <Select
                      size="small"
                      variant="outlined"
                      name="startDay"
                      value={startDay}
                      inputProps={{ "aria-label": t("dayOfWeek") }}
                      onChange={event => {
                        handleChangeSelect(event);
                      }}
                      disabled>
                      {Object.values(dayOfWeek).length > 0 &&
                        Object.values(dayOfWeek).map(day => {
                          return (
                            <MenuItem
                              value={day.cd}
                              key={day.name}>
                              {day.name}
                            </MenuItem>
                          );
                        })}
                    </Select>
                  </FormControl>
                </PanelField> : null}
              <PanelField label={t("createDefaultValue")}>
                <FormControl
                  size="small"
                  sx={{ flex: 1 }}>
                  <Select
                    size="small"
                    variant="outlined"
                    name="initialValue"
                    value={initialValue}
                    inputProps={{ "aria-label": t("createDefaultValue") }}
                    onChange={event => {
                      handleChange(event);
                    }}>
                    {initialValues?.map(initalValue => {
                      return (
                        <MenuItem
                          key={initalValue}
                          value={initalValue}>
                          {t(initalValue)}
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
                {initialValue !== CUSTOMIZATION ? (
                  <Box sx={{ minWidth: "45px", ml: 1 }}>
                    <CalculationButton
                      label="computeExpressSetting"
                      color={computeMeasureList?.length > 0 ? "primary" : "black"}
                      disabled={initialValue === NOTINITIAL}
                      onClick={filterActionItem} />
                  </Box>
                ) : null}
                {initialValue === CUSTOMIZATION ? (
                  <Box sx={{ ml: 1 }}>
                    <DetailButton
                      label="initialPlanDetailSetting"
                      color={"primary"}
                      onClick={clickInitialDetail}
                    />
                  </Box>
                ) : null}
              </PanelField>
              {initialValue === BASELINE_FORECAST || (initialValue === MEASURE_CUSTOM && hasBaselineForecastSource(computeMeasureList)) ?
                <>
                  <PanelField label={t(CUST_LEVEL)}>
                    <FormControl size="small" sx={{ width: 180 }}>
                    <Select
                      id={CUST_LEVEL}
                      variant="outlined"
                      name={CUST_LEVEL}
                      value={!isEmpty(custLevelCd) ? custLevelCd?.id : custLevelCds ? custLevelCds[0]?.id : CUSTOMER.id}
                      inputProps={{ "aria-label": t(CUST_LEVEL) }}
                      onChange={event => {
                        handleChange(event);
                      }}>
                      {custLevelCds?.map(custLevel => {
                        return (
                          <MenuItem
                            key={custLevel.id}
                            value={custLevel.id}>
                            {custLevel.name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                    </FormControl>
                  </PanelField>
                  <PanelField label={t(ITEM_LEVEL)}>
                    <FormControl size="small" sx={{ width: 180 }}>
                    <Select
                      id={ITEM_LEVEL}
                      variant="outlined"
                      name={ITEM_LEVEL}
                      value={!isEmpty(itemLevelCd) ? itemLevelCd?.id : itemLevelCds ? itemLevelCds[0]?.id : ITEM.id}
                      inputProps={{ "aria-label": t(ITEM_LEVEL) }}
                      onChange={event => {
                        handleChange(event);
                      }}>
                      {itemLevelCds?.map(itemLevel => {
                        return (
                          <MenuItem
                            key={itemLevel.id}
                            value={itemLevel.id}>
                            {itemLevel.name}
                          </MenuItem>
                        );
                      })}
                    </Select>
                    </FormControl>
                  </PanelField>
                </> : null}
              {includes(userRoles, roles.APP_BF) ? (
                <Box sx={{ mt: 1 }}>
                  <FormControlLabel
                    sx={{ ...PANEL_CHECKBOX_SX, width: "100%" }}
                    control={
                      <Checkbox
                        name="runBf"
                        checked={runBf}
                        disabled={useCopyBf}
                        onChange={event => handleChangeSelect(event)} />}
                    label={t("runBf")}
                  />
                  <Grid container spacing={2}>
                    {/* 왼쪽 50% : useBfValue 체크박스 */}
                    <Grid item xs={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            name="useBfValue"
                            checked={useCopyBf}
                            disabled={runBf}
                            onChange={event => handleChangeSelect(event)}
                          />
                        }
                        label={t("useBfValue")}
                        sx={{ ...PANEL_CHECKBOX_SX, width: "100%" }}
                      />
                    </Grid>
                    <Grid item xs={6}>
                      {useCopyBf && (
                        <FormControl size="small" fullWidth variant="outlined">
                          <InputLabel id="bfVersion-label">{t("baseForecastVersion")}</InputLabel>
                          <Select
                            labelId="bfVersion-label"
                            variant="outlined"
                            name="bfVersion"
                            label={t("baseForecastVersion")}
                            value={selectBfVersion}
                            onChange={event => handleChangeSelect(event)}
                          >
                            {bfVersionList?.map(ver => (
                              <MenuItem value={ver?.id} key={ver?.name}>
                                {ver?.name}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              ) : null}
              <Box>
                <FormControlLabel
                  control={<Checkbox color="primary" name={"noteFlg"} checked={noteFlg} onChange={event => handleChangeSelect(event)} />}
                  label={t("noteCopy")} key="NOTE_FLG" sx={{ ...PANEL_CHECKBOX_SX, width: "100%" }}
                />
              </Box>
              <Box>
                <FormControlLabel
                  control={<Checkbox color="primary" name={"avgFlg"} checked={avgFlg}
                                     onChange={param => handleChangeSelect(param)}/>}
                  label={t("avgNoOfMonths", { N: avgMonths, measureName: t("avgSalesActual") })} key="AVG_FLG" sx={{ ...PANEL_CHECKBOX_SX, display: "flex" }}
                />
              </Box>
              {codeUtil.SHOW_POS && (
              <Box>
                <FormControlLabel
                  control={<Checkbox color="primary" name={"posAvgFlg"} checked={posAvgFlg}
                                     onChange={param => handleChangeSelect(param)}/>}
                  label={t("avgNoOfMonths", { N: posAvgMonths, measureName: t("posAvgSalesActual") })} key="POS_AVG_FLG" sx={{ ...PANEL_CHECKBOX_SX, display: "flex" }}
                />
              </Box>
              )}
              {/* 신품목/구품목 수요 맵핑은 수요계획 초기값이 최종 릴리즈 버전일 때만 쓸 수 있다 */}
              {isEqual(initialValue, LAST_RELEASED_VERSION) || rollingRange > 0 ? (
                <Box>
                  <FormControlLabel
                    control={<Checkbox color="primary" name={"npiMappingFlg"} checked={npiMappingFlg}
                                       onChange={param => handleChangeSelect(param)}/>}
                    label={t("npiDemandMapping")} key="NPI_MAPPING_FLG" sx={{ ...PANEL_CHECKBOX_SX, display: "flex" }}
                  />
                </Box>
              ) : null}
            </Box>
          </Box>
        </Box>
      </Box>
      <ComputeMeasureModal
        open={filterModalOpen}
        close={handelFilterClose}
        modalResultData={setComputeMeasureList}
        target={initialTarget.length > 0 ? initialTarget : oriMeasureList}
        source={initialMeasureList.length > 0 ? initialMeasureList : oriMeasureList}
        computeList = {computeMeasureList}
      />
      <DpInitialSettingModal
        open={initialSettingOpen}
        close={handelIntialSettingClose}
        itemLevelCds={itemLevelCds}
        custLevelCds={custLevelCds}
        initialValues={initialValues}
        oriMeasureList={oriMeasureList}
        configDatas={customInitialData}
        modalResultData={setCustomInitialData}
        // versionList={versionList}
        posActualFlg={posActualFlg}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </Box>
  );
};

export default withTranslation()(DpVersionModal);
export { DpVersionSchedule };